import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * Swaps in the server-rendered delivery copy when the selected variant changes.
 *
 * The forecast is read from a per-variant metafield in Liquid, so the fetched section HTML
 * already holds the right copy for the newly selected variant. variant-picker.js only morphs
 * the picker itself (see its updateVariantPicker), so blocks outside the picker have to patch
 * themselves from the event payload — same approach as product-price.js.
 */
class ApcDeliveryForecast extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    // Listen via a signal captured now: by disconnect time this element is already detached,
    // so a closest() lookup there returns null and removeEventListener would silently no-op.
    this.#abortController = new AbortController();
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.#updateForecast, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Replaces this block's content with the matching block from the fetched section.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateForecast = (event) => {
    // Ignore updates from another product's picker (cross-sell card, cart-drawer gift, quick add).
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { html } = event.detail.data;
    const blockId = this.dataset.blockId;
    if (!html || !blockId) return;

    // Scope by block id so the label block can't pick up the detail block's copy, and so a
    // foreign forecast earlier in the fetched page is never used.
    const incoming = html.querySelector(`apc-delivery-forecast[data-block-id="${CSS.escape(blockId)}"]`);

    // An absent counterpart means the new variant renders nothing (reason: unknown).
    // Empty this block rather than leaving a stale promise.
    if (!incoming) {
      if (this.innerHTML.trim() !== '') this.innerHTML = '';
      return;
    }

    if (this.innerHTML !== incoming.innerHTML) {
      this.innerHTML = incoming.innerHTML;
    }
  };
}

if (!customElements.get('apc-delivery-forecast')) {
  customElements.define('apc-delivery-forecast', ApcDeliveryForecast);
}
